import { useEffect, useState } from 'react'
import { Link, useParams, useNavigate } from 'react-router-dom'
import {
  CalendarIcon,
  MapPinIcon,
  UserGroupIcon,
  VideoCameraIcon,
  BuildingOfficeIcon,
  ClockIcon,
  ChevronRightIcon,
  DocumentTextIcon,
  ArrowDownTrayIcon,
} from '@heroicons/react/24/outline'
import { CheckCircleIcon } from '@heroicons/react/24/solid'
import { Card, CardHeader, Badge, Avatar, InlineSpinner, EmptyState, Button, Modal, ModalBody, ModalFooter } from '@/components/ui'
import { eventsApi } from '@/api'
import type { Event, EventAgenda, EventMaterial } from '@/api/events'

type EventWithDetails = Event & {
  agenda_items: EventAgenda[]
  materials: EventMaterial[]
  organizer_details?: {
    id: number
    full_name: string
    avatar: string | null
  }
}

export default function EventDetailPage() {
  const { slug } = useParams<{ slug: string }>()
  const navigate = useNavigate()
  const [event, setEvent] = useState<EventWithDetails | null>(null)
  const [loading, setLoading] = useState(true)
  const [registering, setRegistering] = useState(false)
  const [showFeedbackModal, setShowFeedbackModal] = useState(false)
  const [feedbackData, setFeedbackData] = useState({
    overall_rating: 0,
    content_rating: 0,
    organization_rating: 0,
    would_recommend: true,
    comments: '',
  })

  useEffect(() => {
    if (slug) {
      fetchEvent()
    }
  }, [slug])

  const fetchEvent = async () => {
    setLoading(true)
    try {
      const data = await eventsApi.getEvent(slug!)
      setEvent(data)
    } catch (error) {
      console.error('Error fetching event:', error)
    } finally {
      setLoading(false)
    }
  }

  const handleRegister = async () => {
    if (!event) return
    setRegistering(true)
    try {
      await eventsApi.registerForEvent(event.slug)
      fetchEvent()
    } catch (error) {
      console.error('Error registering:', error)
    } finally {
      setRegistering(false)
    }
  }

  const handleCancelRegistration = async () => {
    if (!event) return
    try {
      await eventsApi.cancelRegistration(event.slug)
      fetchEvent()
    } catch (error) {
      console.error('Error cancelling registration:', error)
    }
  }

  const handleJoinMeeting = async () => {
    if (!event) return
    try {
      const data = await eventsApi.getJitsiToken(event.slug)
      // Use JWT URL if available, otherwise use URL with user params, or basic room URL
      const meetingUrl = data.room_url_with_jwt || data.room_url_with_params || data.room_url
      if (meetingUrl) {
        window.open(meetingUrl, '_blank')
      } else {
        console.error('No meeting URL available')
      }
    } catch (error) {
      console.error('Error joining meeting:', error)
    }
  }

  const handleSubmitFeedback = async () => {
    if (!event) return
    try {
      await eventsApi.submitFeedback(event.slug, feedbackData)
      setShowFeedbackModal(false)
    } catch (error) {
      console.error('Error submitting feedback:', error)
    }
  }

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('it-IT', {
      weekday: 'long',
      day: 'numeric',
      month: 'long',
      year: 'numeric',
    })
  }

  const formatTime = (dateString: string) => {
    return new Date(dateString).toLocaleTimeString('it-IT', {
      hour: '2-digit',
      minute: '2-digit',
    })
  }

  const getTypeBadge = (type: string) => {
    const config: Record<string, { variant: 'info' | 'success' | 'warning' | 'default', label: string, icon: typeof VideoCameraIcon }> = {
      online: { variant: 'info', label: 'Online', icon: VideoCameraIcon },
      in_person: { variant: 'success', label: 'In presenza', icon: BuildingOfficeIcon },
      hybrid: { variant: 'warning', label: 'Ibrido', icon: MapPinIcon },
      webinar: { variant: 'info', label: 'Webinar', icon: VideoCameraIcon },
      workshop: { variant: 'success', label: 'Workshop', icon: UserGroupIcon },
      conference: { variant: 'warning', label: 'Conferenza', icon: BuildingOfficeIcon },
      community_day: { variant: 'success', label: 'Community Day', icon: UserGroupIcon },
      training: { variant: 'info', label: 'Formazione', icon: CalendarIcon },
      meeting: { variant: 'default', label: 'Riunione', icon: UserGroupIcon },
      other: { variant: 'default', label: 'Altro', icon: CalendarIcon },
    }
    return config[type] || { variant: 'default' as const, label: type || 'Evento', icon: CalendarIcon }
  }

  const isEventPast = (endDate: string) => new Date(endDate) < new Date()
  const isEventOngoing = (startDate: string, endDate: string) => {
    const now = new Date()
    return new Date(startDate) <= now && now <= new Date(endDate)
  }

  if (loading) {
    return <InlineSpinner message="Caricamento evento..." />
  }

  if (!event) {
    return (
      <EmptyState
        icon={<CalendarIcon className="h-12 w-12" />}
        title="Evento non trovato"
        description="L'evento richiesto non esiste"
        action={{
          label: 'Torna agli eventi',
          onClick: () => navigate('/events'),
        }}
      />
    )
  }

  const typeConfig = getTypeBadge(event.type)
  const TypeIcon = typeConfig.icon
  const isPast = isEventPast(event.end_datetime)
  const isOngoing = isEventOngoing(event.start_datetime, event.end_datetime)
  const isFull = event.max_participants ? (event.registrations_count || 0) >= event.max_participants : false

  return (
    <div className="space-y-6">
      <nav className="flex items-center space-x-2 text-sm text-gray-500">
        <Link to="/events" className="hover:text-primary-600">Eventi</Link>
        <ChevronRightIcon className="h-4 w-4" />
        <span className="text-gray-900 truncate max-w-xs">{event.title}</span>
      </nav>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-6">
          <Card>
            <div className="relative h-64 -mx-4 -mt-4 mb-6 overflow-hidden rounded-t-lg bg-gradient-to-br from-primary-500 to-primary-700">
              {event.image ? (
                <img src={event.image} alt={event.title} className="w-full h-full object-cover" />
              ) : (
                <div className="w-full h-full flex items-center justify-center">
                  <CalendarIcon className="h-24 w-24 text-white/30" />
                </div>
              )}
              <div className="absolute top-4 right-4">
                <Badge variant={typeConfig.variant} size="lg">
                  <TypeIcon className="h-4 w-4 mr-1" />
                  {typeConfig.label}
                </Badge>
              </div>
              {isPast && (
                <div className="absolute inset-0 bg-black/30 flex items-center justify-center">
                  <Badge variant="default" size="lg">Evento concluso</Badge>
                </div>
              )}
              {isOngoing && (
                <div className="absolute bottom-4 left-4">
                  <Badge variant="success" size="lg">In corso</Badge>
                </div>
              )}
            </div>

            <h1 className="text-2xl font-bold text-gray-900 mb-4">{event.title}</h1>

            <div className="flex flex-wrap gap-4 text-sm text-gray-500 mb-6">
              <div className="flex items-center gap-2">
                <CalendarIcon className="h-5 w-5" />
                <span>{formatDate(event.start_datetime)}</span>
              </div>
              <div className="flex items-center gap-2">
                <ClockIcon className="h-5 w-5" />
                <span>{formatTime(event.start_datetime)} - {formatTime(event.end_datetime)}</span>
              </div>
              {event.location && (
                <div className="flex items-center gap-2">
                  <MapPinIcon className="h-5 w-5" />
                  <span>{event.location}</span>
                </div>
              )}
              <div className="flex items-center gap-2">
                <UserGroupIcon className="h-5 w-5" />
                <span>{(event.registrations_count || 0)} iscritti{event.max_participants && ` / ${event.max_participants} posti`}</span>
              </div>
            </div>

            {event.organizer_details && (
              <div className="flex items-center gap-3 pb-6 border-b border-gray-100">
                <Avatar size="md" name={event.organizer_details.full_name} src={event.organizer_details.avatar || undefined} />
                <div>
                  <p className="text-sm text-gray-500">Organizzatore</p>
                  <p className="font-medium text-gray-900">{event.organizer_details.full_name}</p>
                </div>
              </div>
            )}

            <div className="pt-6">
              <h3 className="font-semibold text-gray-900 mb-2">Descrizione</h3>
              <div className="prose prose-sm max-w-none text-gray-600" dangerouslySetInnerHTML={{ __html: event.description || '' }} />
            </div>
          </Card>

          {event.agenda_items && event.agenda_items.length > 0 && (
            <Card>
              <CardHeader title="Programma" />
              <div className="space-y-4">
                {event.agenda_items.map((item) => (
                  <div key={item.id} className="flex gap-4">
                    <div className="flex-shrink-0 w-20 text-sm font-medium text-primary-600">{item.time}</div>
                    <div>
                      <h4 className="font-medium text-gray-900">{item.title}</h4>
                      {item.description && <p className="text-sm text-gray-500 mt-1">{item.description}</p>}
                      {item.speaker && <p className="text-sm text-gray-400 mt-1">Speaker: {item.speaker}</p>}
                    </div>
                  </div>
                ))}
              </div>
            </Card>
          )}

          {event.materials && event.materials.length > 0 && (
            <Card>
              <CardHeader title="Materiali" />
              <div className="divide-y divide-gray-100">
                {event.materials.map((material) => (
                  <div key={material.id} className="flex items-center justify-between py-3">
                    <div className="flex items-center gap-3">
                      <DocumentTextIcon className="h-8 w-8 text-gray-400" />
                      <div>
                        <p className="font-medium text-gray-900">{material.title}</p>
                        <p className="text-xs text-gray-500 uppercase">{material.file_type}</p>
                      </div>
                    </div>
                    <a href={material.file_url} target="_blank" rel="noopener noreferrer" className="p-2 rounded-lg hover:bg-gray-100 transition-colors">
                      <ArrowDownTrayIcon className="h-5 w-5 text-primary-600" />
                    </a>
                  </div>
                ))}
              </div>
            </Card>
          )}
        </div>

        <div className="space-y-6">
          <Card>
            {isPast ? (
              <div className="text-center">
                <p className="text-gray-500 mb-4">Questo evento è terminato</p>
                {event.is_registered && (
                  <Button variant="outline" onClick={() => setShowFeedbackModal(true)}>Lascia feedback</Button>
                )}
              </div>
            ) : event.is_registered ? (
              <div className="space-y-4">
                <div className="flex items-center gap-2 text-green-600">
                  <CheckCircleIcon className="h-6 w-6" />
                  <span className="font-semibold">Sei iscritto!</span>
                </div>
                {(isOngoing || event.type !== 'in_person') && event.jitsi_url && (
                  <Button className="w-full" onClick={handleJoinMeeting}>
                    <VideoCameraIcon className="h-5 w-5 mr-2" />
                    Partecipa ora
                  </Button>
                )}
                <button onClick={handleCancelRegistration} className="w-full text-sm text-gray-500 hover:text-red-600 transition-colors">
                  Annulla iscrizione
                </button>
              </div>
            ) : (
              <div className="space-y-4">
                <div className="text-center">
                  {isFull ? (
                    <p className="text-red-600 font-medium">Posti esauriti</p>
                  ) : (
                    <>
                      <p className="text-sm text-gray-500">Posti disponibili</p>
                      <p className="text-2xl font-bold text-gray-900">
                        {event.max_participants ? event.max_participants - (event.registrations_count || 0) : 'Illimitati'}
                      </p>
                    </>
                  )}
                </div>
                <Button className="w-full" onClick={handleRegister} disabled={isFull || registering}>
                  {registering ? 'Iscrizione in corso...' : 'Iscriviti'}
                </Button>
              </div>
            )}
          </Card>

          <Card>
            <CardHeader title="Informazioni" />
            <dl className="space-y-3 text-sm">
              <div className="flex justify-between">
                <dt className="text-gray-500">Tipo</dt>
                <dd><Badge variant={typeConfig.variant} size="sm">{typeConfig.label}</Badge></dd>
              </div>
              <div className="flex justify-between">
                <dt className="text-gray-500">Data</dt>
                <dd className="font-medium text-gray-900">{formatDate(event.start_datetime)}</dd>
              </div>
              <div className="flex justify-between">
                <dt className="text-gray-500">Orario</dt>
                <dd className="font-medium text-gray-900">{formatTime(event.start_datetime)} - {formatTime(event.end_datetime)}</dd>
              </div>
              <div className="flex justify-between">
                <dt className="text-gray-500">Iscritti</dt>
                <dd className="font-medium text-gray-900">{(event.registrations_count || 0)}</dd>
              </div>
              {event.max_participants && (
                <div className="flex justify-between">
                  <dt className="text-gray-500">Posti totali</dt>
                  <dd className="font-medium text-gray-900">{event.max_participants}</dd>
                </div>
              )}
            </dl>
          </Card>
        </div>
      </div>

      <Modal isOpen={showFeedbackModal} onClose={() => setShowFeedbackModal(false)} title="Valuta l'evento">
        <ModalBody>
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Valutazione generale</label>
              <div className="flex gap-2">
                {[1, 2, 3, 4, 5].map((n) => (
                  <button
                    key={n}
                    onClick={() => setFeedbackData({ ...feedbackData, overall_rating: n })}
                    className={`w-10 h-10 rounded-full border-2 transition-colors ${
                      feedbackData.overall_rating >= n
                        ? 'border-primary-600 bg-primary-600 text-white'
                        : 'border-gray-300 text-gray-500 hover:border-primary-300'
                    }`}
                  >
                    {n}
                  </button>
                ))}
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Commenti (opzionale)</label>
              <textarea
                value={feedbackData.comments}
                onChange={(e) => setFeedbackData({ ...feedbackData, comments: e.target.value })}
                rows={3}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
              />
            </div>
          </div>
        </ModalBody>
        <ModalFooter>
          <Button variant="ghost" onClick={() => setShowFeedbackModal(false)}>Annulla</Button>
          <Button onClick={handleSubmitFeedback} disabled={feedbackData.overall_rating === 0}>Invia feedback</Button>
        </ModalFooter>
      </Modal>
    </div>
  )
}
